import {
  type UseScriptTagOptions,
  useAsyncData,
  useRuntimeConfig,
  useScriptTag,
} from '#imports'
import { type MaybeRef, toValue } from 'vue'

import type {
  PaymentCreateFraudData,
  PaymentMethod,
} from '@backmarket/http-api/src/api-specs-payment/payment'
import { noop } from '@backmarket/utils/function/noop'
import { splitHostname } from '@backmarket/utils/url/splitHostname'
import { v4 as uuid } from 'uuid'

import { SIGNIFYD_LIBRARY_URL } from '../../config/constants'

const useSignifydLibrary = (
  sessionId: string,
  onLibraryLoaded: () => void,
  options: UseScriptTagOptions = {},
) => {
  return useScriptTag(SIGNIFYD_LIBRARY_URL, onLibraryLoaded, {
    ...options,
    attrs: {
      ...options.attrs,
      'data-order-session-id': sessionId,
      id: 'sig-api',
    },
  })
}

// TODO [PAYIN-3532] We should have an API similar to useScriptTag to load Ravelin
// Especially to get retries and logging
const useRavelinLibrary = () => {
  const load = async () => {
    const { default: Ravelin } = await import('ravelinjs/core+track')

    return Ravelin
  }

  return { load }
}

/**
 * Returns a unique session ID for Signifyd. Loads the library if needed.
 */
const useSignifydId = () => {
  const sessionId = uuid()
  const { load, scriptTag } = useSignifydLibrary(sessionId, noop, {
    immediate: false,
    manual: true,
  })

  return async () => {
    if (!scriptTag.value) {
      try {
        await load()

        return sessionId
      } catch (error) {
        // This script can be blocked by extensions like Ghostery.
        // To avoid blocking the payment it will make a silent catch
        // Also, useScriptTag should already have logged the error
      }
    }

    return null
  }
}

/**
 * Returns a unique device ID for Ravelin. Loads the library if needed.
 */
const useRavelinId = () => {
  const {
    public: { RAVELIN_KEY },
  } = useRuntimeConfig()

  const { load } = useRavelinLibrary()

  // Load the library and initialize the instance
  const getRavelinInstance = async (ravelinKey: string) => {
    const Ravelin = await load()

    const { domain, extension } = splitHostname(window.location.host)

    return new Ravelin({
      key: ravelinKey,

      // As recommended, setting the cookie domain at as high a level as possible,
      // e.g. mysite.com rather than subdomain.mysite.com
      // Read more: https://developer.ravelin.com/libraries-and-sdks/ravelinjs/v1/#reference
      cookieDomain: `${domain}.${extension}`,
    })
  }

  return async () => {
    try {
      const ravelinInstance = await getRavelinInstance(RAVELIN_KEY)

      return ravelinInstance.core.id()
    } catch (error) {
      // Silent catch, the payment should not be blocked by this
    }

    return null
  }
}

/**
 * Returns the fraud data needed to create a payment.
 *
 * To assess the probability of fraud, we rely on third party providers.
 * Depending on the payment method, this composable will
 * - load the needed third-party library
 * - get and format the fraud data needed for the payment creation
 */
export const useFraudData = (
  paymentMethod: MaybeRef<PaymentMethod | undefined | null>,
) => {
  const getSignifydSessionId = useSignifydId()
  const getRavelinDeviceId = useRavelinId()

  return useAsyncData(
    async (): Promise<PaymentCreateFraudData> => {
      let signifydSessionId = null
      let ravelinDeviceId = null

      switch (toValue(paymentMethod)?.fraudCheckPartner) {
        case 'signifyd':
          signifydSessionId = await getSignifydSessionId()
          break
        case 'ravelin':
          ravelinDeviceId = await getRavelinDeviceId()
          break
        default:
          break
      }

      return {
        ...(signifydSessionId && { signifyd_fingerprint: signifydSessionId }),
        ...(ravelinDeviceId && { deviceId: ravelinDeviceId }),
      }
    },
    {
      server: false,
    },
  )
}
